import React from 'react';
import { Button } from '@/components/ui/button';
import { Award, ArrowDown } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import { useIsMobile } from '@/hooks/use-mobile';
import { getCheckoutUrlWithUtms } from '@/lib/checkout';

// Placeholder de baixa qualidade para o efeito de blur
const blurDataPlaceholder = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/2wBDAQUFBQcGBw4ICA4eFBEUHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCAAIAAoDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAAAAUH/8QAHxAAAQQCAgMAAAAAAAAAAAAAAQIDBBEABRIhExQx/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAL/xAAaEQACAgMAAAAAAAAAAAAAAAABAgAREjFR/9oADAMBAAIRAxEAPwCW3ZHU+RhtdSm5LnkccXwaaQApZ5qPQA8AHySTQGJdRuNu4w7DUqWA3IIckoAC+I6AI+knGnYqpuWMT//Z";
const ProfessionalHero = () => {
  const scrollToProduct = () => {
    const productSection = document.getElementById('cta');
    if (productSection) {
      productSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const isMobile = useIsMobile();
  return <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-primary overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 opacity-15" style={{
      backgroundImage: "url('/lovable-uploads/c164ff17-6a1f-4ecd-9c01-d90d31b4e0ab.png')",
      backgroundSize: "cover",
      backgroundPosition: "left center",
      backgroundRepeat: "no-repeat"
    }}></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="md:order-1 order-2 text-center md:text-left">
            <div className="inline-block px-4 py-2 mb-4 bg-white/20 backdrop-blur text-white rounded-full mx-auto md:mx-0">
              <span className="text-base md:text-lg font-bold">CURSO PROFISSIONAL</span>
            </div>
            <h1 className="md:text-5xl lg:text-6xl text-white mb-4 leading-tight text-3xl font-semibold text-center md:text-left [text-wrap:balance]">
              Amamentação de <span className="text-white">Ouro</span>
              <span className="block md:text-3xl lg:text-4xl text-2xl md:text-3xl text-accent-light font-bold mt-2">
                Torne-se a referência em amamentação que suas pacientes precisam
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-2 max-w-lg text-center md:text-left">
              O único curso que combina manejo clínico avançado e o Método PPS aplicado em casos reais para profissionais da área materno-infantil que buscam orientar com segurança e se destacar no mercado
            </p>
            <p className="text-sm text-white/70 mb-8 italic text-center md:text-left">
              Certificado de aperfeiçoamento profissional
            </p>
            <div className="flex flex-col sm:flex-row gap-6 items-center justify-center md:justify-start">
              <Button 
                className="btn-hover bg-accent hover:bg-accent-dark text-white font-bold px-10 py-7 text-xl rounded-full shadow-lg" 
                onClick={() => window.location.href = getCheckoutUrlWithUtms('https://pay.hotmart.com/T84177472Y?off=s3ddbg8f&checkoutMode=10')}
              >
                Garantir Curso
              </Button>
              <button 
                className="text-white/80 hover:text-white transition-colors font-medium text-lg flex items-center gap-2 underline underline-offset-4" 
                onClick={() => document.getElementById('course-content')?.scrollIntoView({
                  behavior: 'smooth'
                })}
              >
                <ArrowDown className="h-5 w-5" aria-hidden="true" /> Ver conteúdo completo
              </button>
            </div>
          </div>
          
          <div className="md:order-2 order-1 md:flex md:justify-center hidden">
            <div className="relative">
              <div className="absolute -inset-1 bg-white/20 rounded-full blur-lg"></div>
              <OptimizedImage alt="Rosane Baldissera - Especialista em Amamentação para Profissionais" className="relative z-10 rounded-3xl shadow-2xl w-full max-w-md object-cover animate-fade-in" style={{
              aspectRatio: '3/4'
            }} width={400} height={533} loading="eager" priority={true} blurDataURL={blurDataPlaceholder} src="/lovable-uploads/5539b75f-cdc8-4c11-816a-7ed8d9c62233.png" />
              <div className="absolute -bottom-6 -right-6 bg-secondary-dark rounded-2xl p-4 shadow-lg z-20 animate-slide-in-right">
                <div className="flex items-center gap-3">
                  <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">+2.000 Profissionais</p>
                    <p className="text-white text-sm">já se especializaram</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center md:hidden">
          <div className="relative w-64 h-64">
            <div className="absolute -inset-1 bg-white/20 rounded-full blur-lg"></div>
            <OptimizedImage alt="Rosane Baldissera - Especialista em Amamentação para Profissionais" className="relative z-10 rounded-3xl shadow-2xl w-full h-full object-cover animate-fade-in" src={isMobile ? "/lovable-uploads/c761dc94-4c6b-42b3-917d-32b060eff871.png" : "/lovable-uploads/6df25097-8673-47ce-94a1-5910bd758cca.png"} width={256} height={256} loading="eager" priority={true} blurDataURL={blurDataPlaceholder} />
            <div className="absolute -bottom-6 -right-6 bg-secondary-dark rounded-2xl p-4 shadow-lg z-20 animate-slide-in-right">
              <div className="flex items-center gap-3">
                <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">+2.000 Profissionais</p>
                  <p className="text-white text-sm">já se especializaram</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
    </section>;
};
export default ProfessionalHero;